import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { useHistory } from "react-router-dom";
import { __ } from "services/Translator";
import { useIsAuthenticated } from "components/auth/AuthGate";
import {
  TeaserHero,
  TeaserCTA,
  TeaserSection,
  TeaserGrid,
  FeatureCard,
} from "components/auth/teaser";

// Canonical wheel art — the SAME assets the live gameplay wheel uses
// (elements/WheelOfFortune.js). Marketing == gameplay, so a visitor converted
// by the advertised wheel meets the identical wheel after sign-up.
import diamondWheel from "assets/play365/images/wheels/diamond-wheel.png";
import diamondFrame from "assets/play365/images/wheels/diamond-frame.png";
import goldWheel from "assets/play365/images/wheels/gold-wheel.png";
import silverWheel from "assets/play365/images/wheels/silver-wheel.png";
import bronzeWheel from "assets/play365/images/wheels/bronze-wheel.png";

// Restored brand marketing banner: the golden-clover leprechaun scene with a
// spinning wheel + sparkles that greets visitors at the top of the page. The
// gameplay wheel assets above still drive the tier showcase below.
import wheelBannerScene from "assets/play365/images/wheels/wheel-banner-GCC-full.webp";
import wheelInner from "assets/play365/images/wheels/new_wheel.webp";
import wheelOuter from "assets/play365/images/wheels/outside.webp";

// Wager bands are currency-agnostic numbers rendered in the visitor's locale
// grouping and labelled "in your account currency" — never a baked-in symbol.
// Bands + prize percentages are marketing values; the awarded prizes are the
// backend's (fortune_type.fortune_prizes) source of truth — see crossFile.
const nf = new Intl.NumberFormat();

// Built per render, not at module scope: __() must run after the /translations
// payload is in localStorage or every label freezes to English for the session.
const getTiers = () => [
  {
    id: "bronze",
    label: __("Bronze Wheel"),
    short: __("Bronze"),
    min: 1,
    max: 7499,
    top: "15%",
    prizes: ["15%", "13%", "11%", "9%", "7%", "5%"],
    image: bronzeWheel,
  },
  {
    id: "silver",
    label: __("Silver Wheel"),
    short: __("Silver"),
    min: 7500,
    max: 14999,
    top: "25%",
    prizes: ["25%", "22%", "19%", "16%", "13%", "10%"],
    image: silverWheel,
  },
  {
    id: "gold",
    label: __("Gold Wheel"),
    short: __("Gold"),
    min: 15000,
    max: 24999,
    top: "35%",
    prizes: ["35%", "31%", "27%", "23%", "19%", "15%"],
    image: goldWheel,
  },
  {
    id: "diamond",
    label: __("Diamond Wheel"),
    short: __("Diamond"),
    min: 25000,
    max: null,
    top: "50%",
    prizes: ["50%", "45%", "40%", "35%", "30%", "25%"],
    image: diamondWheel,
  },
];

const bandLabel = (tier) =>
  tier.max == null
    ? `${nf.format(tier.min)}+`
    : `${nf.format(tier.min)} – ${nf.format(tier.max)}`;

// The marquee wheel: the real Diamond gameplay wheel — disc spins inside its
// decorative frame (the frame carries the pointer). No composite/programmer-art
// overlay, no black-void background: the felt glow behind it fills the band.
function WheelHeroVisual() {
  return (
    <div
      className="wheel-hero-visual"
      role="img"
      aria-label={__("Golden Clover Diamond Rakeback Wheel")}
    >
      <span className="wheel-hero-visual__glow" aria-hidden="true" />
      <div className="wheel-hero-visual__medallion" aria-hidden="true">
        <img className="wheel-hero-visual__disc" src={diamondWheel} alt="" />
        <img className="wheel-hero-visual__frame" src={diamondFrame} alt="" />
      </div>
    </div>
  );
}

// Lamp positions around the wheel's rim; each fades in on its own delayed slot.
const WHEEL_SPARKLES = [
  { deg: 23.25, r: 34.85, slot: 4, green: false },
  { deg: 46.25, r: 34.45, slot: 11, green: true },
  { deg: 75.25, r: 33.69, slot: 2, green: false },
  { deg: 106.5, r: 33.21, slot: 9, green: true },
  { deg: 133.5, r: 33.97, slot: 7, green: false },
  { deg: 156.75, r: 33.97, slot: 0, green: false },
  { deg: 180, r: 34.01, slot: 6, green: true },
  { deg: 202.75, r: 33.85, slot: 12, green: false },
  { deg: 226.25, r: 33.57, slot: 3, green: true },
  { deg: 253.5, r: 33.81, slot: 10, green: false },
  { deg: 284.75, r: 33.81, slot: 1, green: true },
  { deg: 313.75, r: 34.37, slot: 8, green: false },
  { deg: 337.25, r: 34.89, slot: 5, green: false },
];

// Full-width marketing banner: leprechaun scene with the inner wheel spinning
// inside the glowing outer ring and lamps twinkling around the rim.
function WheelBannerHero() {
  return (
    <div className="wheel-banner" aria-hidden="true">
      <img className="wheel-banner__scene" src={wheelBannerScene} alt="" />
      <div className="wheel-banner__wheel">
        <img className="wheel-banner__wheel-inner" src={wheelInner} alt="" />
        <img className="wheel-banner__wheel-outer" src={wheelOuter} alt="" />
        <div className="wheel-banner__sparkles">
          {WHEEL_SPARKLES.map((sparkle, index) => (
            <div
              key={index}
              className={
                "wheel-banner__sparkle" +
                (sparkle.green ? " wheel-banner__sparkle--green" : "")
              }
              style={{
                "--lamp-deg": `${sparkle.deg}deg`,
                "--lamp-r": `${sparkle.r}%`,
                "--slot": sparkle.slot,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function TierShowcase() {
  const TIERS = getTiers();
  return (
    <ul className="wheel-showcase">
      {TIERS.map((tier) => (
        <li key={tier.id} className={`wheel-showcase__card wheel-showcase__card--${tier.id}`}>
          <div className="wheel-showcase__art">
            <img src={tier.image} alt="" aria-hidden="true" loading="lazy" />
          </div>
          <div className="wheel-showcase__body">
            <span className="wheel-showcase__tier">{tier.label}</span>
            <span className="wheel-showcase__band">
              {__("Wager")} {bandLabel(tier)}
            </span>
            <span className="wheel-showcase__prize">
              {__("Up to")} <strong>{tier.top}</strong> {__("rakeback")}
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
}

function PrizeTable({ tier, prizeLabels }) {
  return (
    <table className="tier-table">
      <caption className="tier-table__caption">
        {tier.label} — {__("Prizes")}
      </caption>
      <tbody>
        <tr>
          <th scope="row">{__("Wager needed")}</th>
          <td>{bandLabel(tier)}</td>
        </tr>
        {tier.prizes.map((prize, index) => (
          <tr key={index}>
            <th scope="row">{prizeLabels[index]}</th>
            <td>
              {prize} {__("rakeback")}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const getHowItWorks = () => [
  {
    icon: "🎰",
    title: __("Play what you love"),
    desc: __(
      "Every real bet on slots, live tables or sports counts toward the next day's wheel — no opt-in, no coupon code."
    ),
  },
  {
    icon: "🎡",
    title: __("Earn a daily spin"),
    desc: __(
      "Play today and a spin unlocks automatically for the next day. Your wagering decides which wheel you spin — Bronze up to Diamond."
    ),
  },
  {
    icon: "💸",
    title: __("Win instant cash back"),
    desc: __(
      "Spin and the rakeback lands in your balance immediately — real cash, on top of your standard weekly cashback."
    ),
  },
];

export default function Wheel(props) {
  const isAuthenticated = useIsAuthenticated();
  const history = useHistory();
  const [activeTier, setActiveTier] = useState(0);
  const TIERS = getTiers();
  const HOW_IT_WORKS = getHowItWorks();
  const active = TIERS[activeTier];

  const prizeLabels = [
    __("Top Prize"),
    __("2nd Prize"),
    __("3rd Prize"),
    __("4th Prize"),
    __("5th Prize"),
    __("6th Prize"),
  ];

  // Working conversion CTA. The FullLayout auth/modal setters reach this page
  // via passableParams (cloneElement); if one is ever missing the button still
  // navigates to /register instead of silently no-opping.
  const pulse = (setter) => {
    if (typeof setter === "function") {
      setter(true);
      setTimeout(() => setter(false), 500);
      return true;
    }
    return false;
  };
  const openSignUp = () => {
    if (!pulse(props.setModalSignUpPropsData)) history.push("/register");
  };
  const openLogin = () => {
    if (!pulse(props.setModalLoginPropsData)) history.push("/register");
  };
  const goDeposit = () => {
    if (typeof props.setOpenWalletModalTab === "function") {
      props.setOpenWalletModalTab("deposit");
    } else {
      history.push("/");
    }
  };

  const handleTabKey = (event) => {
    const keys = ["ArrowRight", "ArrowLeft", "Home", "End"];
    if (!keys.includes(event.key)) return;
    event.preventDefault();
    const count = TIERS.length;
    let next = activeTier;
    if (event.key === "ArrowRight") next = (activeTier + 1) % count;
    else if (event.key === "ArrowLeft") next = (activeTier - 1 + count) % count;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = count - 1;
    setActiveTier(next);
    const el = document.getElementById("wheel-tier-tab-" + TIERS[next].id);
    if (el) el.focus();
  };

  return (
    <>
      <Helmet>
        <title>{__("Daily Rakeback Wheel | Golden Clover Casino")}</title>
        <meta name="keywords" content={require("elements/Keywords").default} />
        <meta
          name="description"
          content={__(
            "Spin the Golden Clover Rakeback Wheel every day — Bronze to Diamond tiers pay up to 50% cash back on your play."
          )}
        />
        <link
          rel="canonical"
          href={"https://goldenclovercasino.com" + window.location.pathname}
        />
      </Helmet>

      <div className="wheel_container">
        <div className="container-xxl">
          <WheelBannerHero />
          {isAuthenticated ? (
            <section className="wheel-member-hero">
              <div className="wheel-member-hero__copy">
                <span className="teaser-eyebrow">{__("DAILY RAKEBACK WHEEL")}</span>
                <h1 className="wheel-member-hero__title">
                  {__("Your play always")} <span>{__("pays back")}</span>
                </h1>
                <p className="wheel-member-hero__sub">
                  {__(
                    "Play today to unlock tomorrow's spin. Your wagering sets your wheel — the more you play, the higher the tier and the bigger the cash rakeback."
                  )}
                </p>
                <div className="wheel-member-hero__actions">
                  <button
                    type="button"
                    className="commonBtn pOneBtn"
                    onClick={goDeposit}
                  >
                    {__("Deposit & play")}
                  </button>
                </div>
              </div>
            </section>
          ) : (
            <TeaserHero
              align="start"
              eyebrow={__("DAILY RAKEBACK WHEEL")}
              title={
                <>
                  {__("Every day you play")}{" "}
                  <span>{__("pays you back")}</span>
                </>
              }
              subtitle={__(
                "Join Golden Clover Casino and spin the Rakeback Wheel every day — climb from Bronze to Diamond and win up to 50% cash back on your play, credited instantly."
              )}
              onSignUp={openSignUp}
              onLogin={openLogin}
              signUpLabel={__("Join Golden Clover Casino")}
            >
              <p className="wheel-trust">
                {__("18+")} · {__("Terms & Conditions apply")} ·{" "}
                {__("Please play responsibly")}
              </p>
            </TeaserHero>
          )}

          <TeaserSection
            eyebrow={__("HOW IT WORKS")}
            title={__("Three steps to daily cash back")}
          >
            <TeaserGrid minColWidth={260} gap={20}>
              {HOW_IT_WORKS.map((step) => (
                <FeatureCard
                  key={step.title}
                  icon={step.icon}
                  title={step.title}
                  description={step.desc}
                />
              ))}
            </TeaserGrid>
          </TeaserSection>

          <TeaserSection
            eyebrow={__("FOUR TIERS")}
            title={__("The more you play, the bigger the wheel")}
            subtitle={__(
              "Your wagering only sets which wheel you spin — it never affects your loyalty level. Every tier pays cash rakeback on top of your standard weekly cashback."
            )}
          >
            <TierShowcase />
            <p className="wheel-currency-note">
              {__("Wager totals are shown in your account currency.")}
            </p>
          </TeaserSection>

          <div className="vip_club_area wheel-table-area">
            <div className="wheel-tier-switcher">
              <h2 className="wheel-tier-switcher__title">
                {__("Tiers & prizes")}
              </h2>
              <div
                className="wheel-tier-tabs"
                role="tablist"
                aria-label={__("Choose a wheel tier")}
              >
                {TIERS.map((tier, index) => (
                  <button
                    key={tier.id}
                    type="button"
                    role="tab"
                    id={"wheel-tier-tab-" + tier.id}
                    aria-selected={activeTier === index}
                    aria-controls="wheel-tier-panel"
                    tabIndex={activeTier === index ? 0 : -1}
                    className={
                      "wheel-tier-tab" +
                      (activeTier === index ? " is-active" : "")
                    }
                    onClick={() => setActiveTier(index)}
                    onKeyDown={handleTabKey}
                  >
                    {tier.short}
                  </button>
                ))}
              </div>

              <div
                id="wheel-tier-panel"
                role="tabpanel"
                aria-labelledby={"wheel-tier-tab-" + active.id}
                className="wheel-tier-panel"
              >
                <div className="wheel-tier-panel__table">
                  <PrizeTable tier={active} prizeLabels={prizeLabels} />
                </div>
                <div className="wheel-tier-panel__art">
                  <img
                    className="vipBanner"
                    alt={active.label}
                    src={active.image}
                  />
                </div>
              </div>
            </div>

            <p className="wheel-fineprint">
              <b>{__("Note:")}</b>{" "}
              {__(
                "Wagering only determines which wheel you spin and does not affect your loyalty level. We may modify or end this promotion at any time in accordance with the general (promo) terms."
              )}
            </p>

            <div className="wheel-cta">
              {isAuthenticated ? (
                <button
                  type="button"
                  className="commonBtn pOneBtn"
                  onClick={goDeposit}
                >
                  {__("Deposit & play")}
                </button>
              ) : (
                <TeaserCTA
                  size="lg"
                  align="center"
                  onSignUp={openSignUp}
                  onLogin={openLogin}
                  signUpLabel={__("Join Golden Clover Casino")}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
